<template>
    <div class="border-b border-gray-400 py-2">
        <div class="group flex flex-col md:flex-row items-start md:items-center justify-between"
            @click="showActions"
        >
            <div v-if="! is_editing"
                class="cursor-pointer flex flex-1 items-center leading-snug group pr-6 pt-1 pb-0.5"
                :class="is_dragging ? 'bg-blue-100 rounded' : ''"
                draggable
                :data-document-id="document.id"
                @dragstart="startDrag"
                @dragend="endDrag"
            >
                <span class="flex items-start mr-4 group-hover:text-blue-500">
                    <file-icon size="14" class="inline text-gray-500 group-hover:text-blue-400 mr-2 mt-0.5" />

                    <div class="font-semibold text-base leading-snug select-none">
                        {{ display_filename }} <span class="font-normal text-gray-600 text-xs ml-3 mt-1">{{ document.size_for_humans }}</span>
                    </div>
                </span>
            </div>

            <div v-else
                class="flex flex-1 items-center leading-snug pr-6"
            >
                <span class="flex-1 flex items-start mr-4 group-hover:text-blue-500">
                    <file-icon size="14" class="inline text-green-warm-600 mr-2 mt-1.5" />

                    <input class="flex-1 text-black text-base mr-4 p-0.5 border border-gray-400"
                        ref="input"
                        v-model="new_filename"
                        spellcheck="false"
                        @keydown.enter.prevent="saveFilename"
                        @keydown.escape.prevent="cancelEditFilename"
                    />
                </span>
            </div>

            <div class="flex items-center text-gray-600 group-hover:text-blue-500">
                <div v-if="document.docusign_document" class="mr-2">
                    <i class="fa fa-pen-nib"></i>
                </div>

                <div class="flex items-center justify-center w-5 h-5 rounded-full border border-gray-500 group-hover:bg-blue-100">
                    <more-horizontal-icon size="14" />
                </div>
            </div>
        </div>

        <div v-if="is_editing" class="w-full md:w-auto flex pt-1">
            <div class="flex flex-1 items-center justify-start h-12 space-x-4 pt-2 pl-3">
                <div class="text-xs text-gray-600 hover:text-gray-700 cursor-pointer"
                    @click="cancelEditFilename"
                >
                    <i class="far fa-times-circle mr-1"></i>
                    Cancel
                </div>

                <div class="flex items-center h-8 text-xs text-green-warm-600 hover:text-green-warm-800 cursor-pointer px-3 rounded border border-green-warm-600 hover:border-green-warm-700 hover:bg-green-warm-100"
                    @click="saveFilename"
                >
                    <i class="far fa-check-circle mr-1 pb-0.5"></i>
                    Save Changes
                </div>
            </div>
        </div>

        <div v-if="is_showing_actions && ! is_editing" class="w-full md:w-auto flex pt-1">
            <div v-if="! show_delete_confirmation"
                class="flex flex-1 h-12 space-x-3 pt-2"
            >
                <div class="flex flex-1 items-center justify-between pl-3">
                    <div class="flex items-center">
                        <div class="flex-none text-xs text-gray-600 hover:text-blue-600 cursor-pointer py-1 px-2 rounded"
                            @click="editFilename"
                        >
                            <i class="far fa-edit mr-1"></i>
                            Rename
                        </div>

                        <div class="flex-none text-xs text-gray-600 hover:text-red-600 cursor-pointer py-1 px-2 rounded"
                            @click="confirmDelete"
                        >
                            <i class="far fa-trash-alt mr-1"></i>
                            Delete
                        </div>
                    </div>

                    <div class="space-x-3 flex items-center">
                        <a v-if="document.is_viewable"
                            class="flex items-center h-8 text-xs text-green-warm-600 hover:text-green-warm-800 cursor-pointer px-3 rounded border border-green-warm-600 hover:border-green-warm-700 hover:bg-green-warm-100"
                            :href="document.show_route"
                            target="_blank"
                        >
                            <i class="far fa-eye mr-1 pb-0.5"></i>
                            View
                        </a>

                        <a v-if="document.is_viewable_via_google"
                            class="leading-snug flex items-center h-8 text-xs text-green-warm-600 hover:text-green-warm-800 cursor-pointer px-3 rounded border border-green-warm-600 hover:border-green-warm-700 hover:bg-green-warm-100"
                            :href="document.show_inline_route"
                        >
                            <i class="far fa-eye mr-1.5 pb-0.5"></i>
                            View
                        </a>

                        <a class="leading-snug flex items-center h-8 text-xs text-blue-500 hover:text-blue-700 cursor-pointer px-3 rounded border border-blue-500 hover:border-blue-700 hover:bg-blue-100"
                            :href="document.download_route"
                        >
                            <i class="far fa-arrow-alt-circle-down mr-1.5 pb-0.5"></i>
                            Download
                        </a>
                    </div>
                </div>
            </div>

            <div v-else
                class="flex flex-1 items-center h-12 space-x-6 pt-2 pl-2"
            >
                <div class="text-sm font-semibold text-red-600 pb-0.5">
                    Delete this file?
                </div>

                <div class="text-xs text-gray-600 hover:text-gray-700 cursor-pointer"
                    @click="show_delete_confirmation = false"
                >
                    <i class="far fa-times-circle mr-1"></i>
                    Cancel
                </div>

                <div class="flex items-center h-8 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 cursor-pointer px-3 rounded border border-red-600 hover:border-red-700"
                    @click="deleteFile"
                >
                    <i class="far fa-trash-alt mr-1"></i>
                    Delete
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FileIcon, MoreHorizontalIcon } from 'vue-feather-icons';

export default {
    components: {
        FileIcon,
        MoreHorizontalIcon,
    },

    props: {
        document: {},
        signatures_enabled: { default: false },
    },

    data() {
        return {
            is_showing_actions: false,
            is_editing: false,
            is_dragging: false,
            show_delete_confirmation: false,
            display_filename: this.document.display_filename,
            new_filename: this.document.title || this.document.filename,
        }
    },

    methods: {
        showActions() {
            this.is_showing_actions = ! this.is_showing_actions;
        },
        editFilename() {
            this.is_editing = true;
            this.$nextTick(() => {
                this.$refs.input?.focus();
            });
        },
        cancelEditFilename() {
            this.is_editing = false;
            this.new_filename = this.document.title || this.document.filename;
        },
        saveFilename() {
            this.display_filename = this.new_filename;

            axios.patch(this.document.update_route, {
                    title: this.new_filename,
                })
                .then(response => {
                    this.is_editing = false;
                    this.$emit('edited', this.document.id);
                })
                .catch(error => {
                    console.log(error);
                });
        },
        confirmDelete() {
            this.show_delete_confirmation = true;
        },
        deleteFile() {
            axios.delete(this.document.delete_route)
                .then(response => {
                    this.$emit('deleted', this.document.id)
                })
                .catch(error => {
                    console.log(error);
                });
        },
        startDrag(event) {
            this.is_dragging = true;
            event.dataTransfer.dropEffect = 'move';
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('itemId', event.target.dataset.documentId);
        },
        endDrag(event) {
            this.is_dragging = false;
        },
    },
}
</script>
